"use client"

import { SideSheetBody } from "@opensea/ui-kit"
import { isAddressEqual } from "@opensea/utils"
import { Suspense } from "react"
import { UnreachableCaseError } from "../../../lib/errors"
import { useAccount } from "../../../polychain/hooks"
import type { Address } from "../../../types"
import { ViewWalletHeader } from "./header/ViewWalletHeader"
import { ViewWalletTabMenu } from "./header/ViewWalletTabMenu"
import { CryptoTab, CryptoTabSkeleton } from "./tabs/CryptoTab/CryptoTab"
import { NftTab, NftTabSkeleton } from "./tabs/NftTab/NftTab"
import type { ViewWalletTab } from "./types"

type ViewWalletFlowContentProps = {
  address: Address
  tab: ViewWalletTab
  switchTab: (tab: ViewWalletTab) => void
}

export const ViewWalletFlowContent = ({
  address,
  tab,
  switchTab,
}: ViewWalletFlowContentProps) => {
  return (
    <>
      <ViewWalletHeader address={address} />
      <ViewWalletTabMenu
        activeTab={tab}
        address={address}
        switchTab={switchTab}
      />
      <SideSheetBody className="flex flex-col gap-4 px-0 pt-6 lg:px-0">
        <TabContentSwitch address={address} tab={tab} />
      </SideSheetBody>
    </>
  )
}

const TabContentSwitch = ({
  address,
  tab,
}: Omit<ViewWalletFlowContentProps, "switchTab">) => {
  const { address: connectedAddress } = useAccount()
  const isProfileOwner = isAddressEqual(connectedAddress, address)
  switch (tab) {
    case "crypto":
      return (
        <Suspense
          fallback={<CryptoTabSkeleton isProfileOwner={isProfileOwner} />}
        >
          <CryptoTab address={address} isProfileOwner={isProfileOwner} />
        </Suspense>
      )
    case "nfts":
      return (
        <Suspense fallback={<NftTabSkeleton />}>
          <NftTab address={address} />
        </Suspense>
      )
    default:
      throw new UnreachableCaseError(tab)
  }
}
